import { defineStore } from 'pinia'
import { fetchCurrentUser, getAdminEmail, getAdminSessionToken, saveCurrentUserProfile, setAdminEmail } from '../services/repodepot-api'

export type ProfileAvatarPreset = {
  value: string
  label: string
  color: string
  categoryId: string
}

export type ProfileAvatarCategory = {
  id: string
  label: string
  color: string
  avatars: ProfileAvatarPreset[]
}

export type ProfileAvatarBackgroundColor = {
  value: string
  label: string
}

type ProfileAvatarSource = {
  avatar_preset?: string | null
  avatar_background_color?: string | null
}

export const PROFILE_AVATAR_PRESET_STORAGE_KEY = 'rd_profile_avatar_preset'
export const PROFILE_AVATAR_BACKGROUND_COLOR_STORAGE_KEY = 'rd_profile_avatar_background_color'
export const DEFAULT_PROFILE_AVATAR_PRESET = '😍'
export const DEFAULT_PROFILE_AVATAR_BACKGROUND_COLOR = '#FFD952'
export const PROFILE_AVATAR_LIBRARY_MINIMUM_COUNT = 1000

export const PROFILE_AVATAR_BACKGROUND_COLORS: ProfileAvatarBackgroundColor[] = [
  { value: '#FFD952', label: 'Gold' },
  { value: '#5FD1FF', label: 'Sky' },
  { value: '#43DCA6', label: 'Mint' },
  { value: '#735BF2', label: 'Indigo' },
  { value: '#F76E7C', label: 'Coral' },
  { value: '#F39C12', label: 'Amber' },
  { value: '#2DD4BF', label: 'Teal' },
  { value: '#C4B5FD', label: 'Lavender' },
  { value: '#F8FAFC', label: 'Pearl' },
  { value: '#1E293B', label: 'Slate' },
]

type ProfileAvatarCategorySpec = {
  id: string
  label: string
  color: string
  values: string
}

const PROFILE_AVATAR_CATEGORY_SPECS: ProfileAvatarCategorySpec[] = [
  {
    id: 'faces',
    label: 'Faces',
    color: 'warning',
    values:
      '😀 😃 😄 😁 😆 😅 😂 🙂 🙃 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😙 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🫢 🫣 🤫 🤔 🫡 🤐 🤨 😐 😑 😶 🫥 😏 😒 🙄 😬 😮‍💨 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 🥸 😎 🤓 🧐 😕 🫤 😟 🙁 ☹️ 😮 😯 😲 😳 🥺 🥹 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿 💀 ☠️',
  },
  {
    id: 'people',
    label: 'People',
    color: 'info',
    values:
      '👶 🧒 👦 👧 🧑 👱 👨 👩 🧔 👴 👵 👲 👳 🧕 👮 👷 💂 🕵️ 👩‍⚕️ 👨‍⚕️ 👩‍🎓 👨‍🎓 👩‍🏫 👨‍🏫 👩‍⚖️ 👨‍⚖️ 👩‍🌾 👨‍🌾 👩‍🍳 👨‍🍳 👩‍🔧 👨‍🔧 👩‍🏭 👨‍🏭 👩‍💼 👨‍💼 👩‍🔬 👨‍🔬 👩‍💻 👨‍💻 👩‍🎤 👨‍🎤 👩‍🎨 👨‍🎨 👩‍✈️ 👨‍✈️ 👩‍🚀 👨‍🚀 👩‍🚒 👨‍🚒 🧙 🧚 🧛 🧜 🧝 🧞 🧟 🦸 🦹 🥷 🤴 👸 🤵 👰 🧑‍🎄 🎅 🤶 🧑‍🚀 🧑‍💻 🧑‍🔬 🧑‍🎨 🧑‍🏫 🧑‍🍳 🧑‍🔧 🧑‍🌾 🧑‍⚖️',
  },
  {
    id: 'animals',
    label: 'Animals',
    color: 'success',
    values:
      '🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐻‍❄️ 🐨 🐯 🦁 🐮 🐷 🐽 🐸 🐵 🙈 🙉 🙊 🐒 🐔 🐧 🐦 🐤 🐣 🐥 🦆 🦅 🦉 🦇 🐺 🐗 🐴 🦄 🐝 🪲 🐞 🦋 🐌 🐛 🦟 🦗 🕷️ 🕸️ 🦂 🐢 🐍 🦎 🦖 🦕 🐙 🦑 🦐 🦞 🦀 🪼 🐡 🐠 🐟 🐬 🐳 🐋 🦈 🦭 🐊 🐅 🐆 🦓 🦍 🦧 🦣 🐘 🦛 🦏 🐪 🐫 🦒 🦘 🦬 🐃 🐂 🐄 🐎 🐖 🐏 🐑 🦙 🐐 🦌 🐕 🐩 🦮 🐕‍🦺 🐈 🐈‍⬛ 🪶 🐓 🦃 🦤 🦚 🦜 🦢 🦩 🕊️ 🐇 🦝 🦨 🦡 🦫 🦦 🦥 🐁 🐀 🐿️ 🦔',
  },
  {
    id: 'nature',
    label: 'Nature',
    color: '#43dca6',
    values:
      '🌵 🎄 🌲 🌳 🌴 🪵 🌱 🌿 ☘️ 🍀 🎍 🪴 🎋 🍃 🍂 🍁 🍄 🐚 🪨 🌾 💐 🌷 🪷 🌹 🥀 🌺 🌸 🌼 🌻 🌞 🌝 🌛 🌜 🌚 🌕 🌖 🌗 🌘 🌑 🌒 🌓 🌔 🌙 🌎 🌍 🌏 🪐 💫 ⭐ 🌟 ✨ ⚡ ☄️ 💥 🔥 🌪️ 🌈 ☀️ 🌤️ ⛅ 🌥️ ☁️ 🌦️ 🌧️ ⛈️ 🌩️ 🌨️ ❄️ ☃️ ⛄ 🌬️ 💨 💧 💦 ☔ ☂️ 🌊 🌫️',
  },
  {
    id: 'food',
    label: 'Food',
    color: 'danger',
    values:
      '🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🫐 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🫒 🥑 🍆 🥔 🥕 🌽 🌶️ 🫑 🥒 🥬 🥦 🧄 🧅 🍄 🥜 🫘 🌰 🍞 🥐 🥖 🫓 🥨 🥯 🥞 🧇 🧀 🍖 🍗 🥩 🥓 🍔 🍟 🍕 🌭 🥪 🌮 🌯 🫔 🥙 🧆 🥚 🍳 🥘 🍲 🫕 🥣 🥗 🍿 🧈 🧂 🥫 🍱 🍘 🍙 🍚 🍛 🍜 🍝 🍠 🍢 🍣 🍤 🍥 🥮 🍡 🥟 🥠 🥡 🦪 🍦 🍧 🍨 🍩 🍪 🎂 🍰 🧁 🥧 🍫 🍬 🍭 🍮 🍯 🍼 🥛 ☕ 🫖 🍵 🍶 🍾 🍷 🍸 🍹 🍺 🍻 🥂 🥃 🫗 🥤 🧋 🧃 🧉 🧊',
  },
  {
    id: 'activities',
    label: 'Activities',
    color: 'primary',
    values:
      '⚽ 🏀 🏈 ⚾ 🥎 🎾 🏐 🏉 🥏 🎱 🪀 🏓 🏸 🏒 🏑 🥍 🏏 🪃 🥅 ⛳ 🪁 🏹 🎣 🤿 🥊 🥋 🎽 🛹 🛼 🛷 ⛸️ 🥌 🎿 ⛷️ 🏂 🪂 🏋️ 🤼 🤸 ⛹️ 🤺 🤾 🏌️ 🏇 🧘 🏄 🏊 🤽 🚣 🧗 🚴 🚵 🎪 🎭 🩰 🎨 🎬 🎤 🎧 🎼 🎹 🥁 🪘 🎷 🎺 🪗 🎸 🪕 🎻 🎲 ♟️ 🎯 🎳 🎮 🎰 🧩',
  },
  {
    id: 'travel',
    label: 'Travel',
    color: '#5fd1ff',
    values:
      '🚗 🚕 🚙 🚌 🚎 🏎️ 🚓 🚑 🚒 🚐 🛻 🚚 🚛 🚜 🏍️ 🛵 🚲 🛴 🛹 🛼 🚏 🛣️ 🛤️ 🛢️ ⛽ 🛞 🚨 🚥 🚦 🛑 🚧 ⚓ 🛟 ⛵ 🛶 🚤 🛳️ ⛴️ 🚢 ✈️ 🛩️ 🛫 🛬 🪂 💺 🚁 🚟 🚠 🚡 🛰️ 🚀 🛸 🛎️ 🧳 ⌛ ⏳ ⌚ ⏰ ⏱️ ⏲️ 🕰️ 🌍 🌎 🌏 🗺️ 🧭 🏔️ ⛰️ 🌋 🗻 🏕️ 🏖️ 🏜️ 🏝️ 🏞️ 🏟️ 🏛️ 🏗️ 🧱 🪨 🪵 🛖 🏘️ 🏚️ 🏠 🏡 🏢 🏣 🏤 🏥 🏦 🏨 🏩 🏪 🏫 🏬 🏭 🏯 🏰 💒 🗼 🗽 ⛪ 🕌 🛕 🕍 ⛩️ 🕋 ⛲ ⛺ 🌁 🌃 🏙️ 🌄 🌅 🌆 🌇 🌉 ♨️ 🎠 🛝 🎡 🎢 💈',
  },
  {
    id: 'objects',
    label: 'Objects',
    color: '#735BF2',
    values:
      '💎 🔔 🔕 🎼 🎵 🎶 🎙️ 🎚️ 🎛️ 🎤 🎧 📻 🎷 🪗 🎸 🎹 🎺 🎻 🪕 🥁 🪘 📱 📲 ☎️ 📞 📟 📠 🔋 🪫 🔌 💻 🖥️ 🖨️ ⌨️ 🖱️ 🖲️ 💽 💾 💿 📀 🧮 🎥 🎞️ 📽️ 🎬 📺 📷 📸 📹 📼 🔍 🔎 🕯️ 💡 🔦 🏮 🪔 📔 📕 📖 📗 📘 📙 📚 📓 📒 📃 📜 📄 📰 🗞️ 📑 🔖 🏷️ 💰 🪙 💴 💵 💶 💷 💸 💳 🧾 💹 ✉️ 📧 📨 📩 📤 📥 📦 📫 📪 📬 📭 📮 🗳️ ✏️ ✒️ 🖋️ 🖊️ 🖌️ 🖍️ 📝 💼 📁 📂 🗂️ 📅 📆 🗒️ 🗓️ 📇 📈 📉 📊 📋 📌 📍 📎 🖇️ 📏 📐 ✂️ 🗃️ 🗄️ 🗑️ 🔒 🔓 🔏 🔐 🔑 🗝️ 🔨 🪓 ⛏️ ⚒️ 🛠️ 🗡️ ⚔️ 🔫 🪃 🛡️ 🪚 🔧 🪛 🔩 ⚙️ 🗜️ ⚖️ 🦯 🔗 ⛓️ 🪝 🧰 🧲 🪜',
  },
  {
    id: 'symbols',
    label: 'Symbols',
    color: 'secondary',
    values:
      '❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 ❣️ 💕 💞 💓 💗 💖 💘 💝 💟 ☮️ ✝️ ☪️ 🕉️ ☸️ ✡️ 🔯 🕎 ☯️ ☦️ 🛐 ⛎ ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓ 🆔 ⚛️ 🉑 ☢️ ☣️ 📴 📳 🈶 🈚 🈸 🈺 🈷️ ✴️ 🆚 💮 🉐 ㊙️ ㊗️ 🈴 🈵 🈹 🈲 🅰️ 🅱️ 🆎 🆑 🅾️ 🆘 ❌ ⭕ 🛑 ⛔ 📛 🚫 💯 💢 ♨️ 🚷 🚯 🚳 🚱 🔞 📵 🚭 ❗ ❕ ❓ ❔ ‼️ ⁉️ 🔅 🔆 〽️ ⚠️ 🚸 🔱 ⚜️ 🔰 ♻️ ✅ 🈯 💹 ❇️ ✳️ ❎ 🌐 💠 Ⓜ️ 🌀 💤 🏧 🚾 ♿ 🅿️ 🛗 🈳 🈂️ 🛂 🛃 🛄 🛅 🚹 🚺 🚼 ⚧️ 🚻 🚮 🎦 📶 🈁 🔣 ℹ️ 🔤 🔡 🔠 🆖 🆗 🆙 🆒 🆕 🆓 0️⃣ 1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 6️⃣ 7️⃣ 8️⃣ 9️⃣ 🔟',
  },
]

export const PROFILE_AVATAR_COMPOSITE_BADGES = '✨ ⚡ 🔹 🔸 🟢 🔵 🟣 🟡 🔺 ⭐'

export const PROFILE_AVATAR_COMPOSITE_CATEGORY_SPECS: ProfileAvatarCategorySpec[] = [
  { id: 'agent-core', label: 'Agent Core', color: 'primary', values: '🤖 🦾 🧠 🧬 🛰️ 🛡️ ⚙️ 💻 🧭 🔬 🧰 🗝️' },
  { id: 'agent-faces', label: 'Agent Faces', color: 'warning', values: '😀 😎 🤓 🧐 🤠 🥳 🥸 🤩 🫡 🤔 🤗 😈' },
  { id: 'agent-people', label: 'Agent People', color: 'info', values: '🧑‍💻 🧑‍🚀 🧑‍🔬 🧑‍🎨 🧑‍🏫 🧑‍🍳 🧑‍🔧 🧑‍🌾 🧑‍⚖️ 🥷 🕵️ 🦸' },
  { id: 'agent-animals', label: 'Agent Animals', color: 'success', values: '🐺 🦊 🐯 🦁 🐼 🐨 🦉 🦅 🐙 🦈 🦄 🐉' },
  { id: 'agent-nature', label: 'Agent Nature', color: '#43dca6', values: '🌲 🌵 🌊 🔥 ❄️ ⚡ 🌙 ☀️ 🌈 🌋 🪐 🌌' },
  { id: 'agent-food', label: 'Agent Food', color: 'danger', values: '🍎 🍋 🍉 🍇 🍓 🥑 🌶️ 🍕 🍣 🍩 ☕ 🧋' },
  { id: 'agent-activities', label: 'Agent Activities', color: 'secondary', values: '⚽ 🏀 🎾 🥊 🏹 🎨 🎮 🧩 🎯 🎸 🎹 🎭' },
  { id: 'agent-travel', label: 'Agent Travel', color: '#5fd1ff', values: '🚗 🏎️ 🚲 🚀 ✈️ 🚁 🚢 🗺️ 🏕️ 🏔️ 🗽 🏰' },
  { id: 'agent-objects', label: 'Agent Objects', color: '#735BF2', values: '🖥️ 🖨️ 📷 🔦 📚 ✒️ 🔑 🔨 🧲 🧪 🪄 🔭' },
  { id: 'agent-symbols', label: 'Agent Symbols', color: 'secondary', values: '❤️ 💛 💚 💙 💜 🖤 🤍 💠 ♻️ ✅ 🔰 ☯️' },
]

function parseAvatarValues(values: string): string[] {
  return values.trim().split(/\s+/).filter(Boolean)
}

function buildCompositeAvatarCategories(): ProfileAvatarCategory[] {
  const badges = parseAvatarValues(PROFILE_AVATAR_COMPOSITE_BADGES)
  return PROFILE_AVATAR_COMPOSITE_CATEGORY_SPECS.map((category) => ({
    id: category.id,
    label: category.label,
    color: category.color,
    avatars: parseAvatarValues(category.values).flatMap((base, baseIndex) => badges.map((badge, badgeIndex) => ({
      value: `${base}${badge}`,
      label: `${category.label} ${baseIndex + 1}.${badgeIndex + 1}`,
      color: category.color,
      categoryId: category.id,
    }))),
  }))
}

const profileAvatarBaseCategories: ProfileAvatarCategory[] = PROFILE_AVATAR_CATEGORY_SPECS.map((category) => ({
  id: category.id,
  label: category.label,
  color: category.color,
  avatars: parseAvatarValues(category.values).map((value, index) => ({
    value,
    label: `${category.label} ${index + 1}`,
    color: category.color,
    categoryId: category.id,
  })),
}))

export const PROFILE_AVATAR_CATEGORIES: ProfileAvatarCategory[] = [
  ...profileAvatarBaseCategories,
  ...buildCompositeAvatarCategories(),
]

export const PROFILE_AVATAR_PRESETS: ProfileAvatarPreset[] = PROFILE_AVATAR_CATEGORIES.flatMap((category) => category.avatars)
const uniqueProfileAvatarPresetCount = new Set(PROFILE_AVATAR_PRESETS.map((preset) => preset.value)).size

if (uniqueProfileAvatarPresetCount < PROFILE_AVATAR_LIBRARY_MINIMUM_COUNT) {
  throw new Error(`Profile avatar library requires at least ${PROFILE_AVATAR_LIBRARY_MINIMUM_COUNT} unique presets`)
}

export function resolveProfileAvatarPreset(value: string): ProfileAvatarPreset {
  const normalized = String(value ?? '').trim()
  return PROFILE_AVATAR_PRESETS.find((preset) => preset.value === normalized) ?? PROFILE_AVATAR_PRESETS[0]
}

export function resolveProfileAvatarBackgroundColor(value: string): ProfileAvatarBackgroundColor {
  const normalized = String(value ?? '').trim().toLowerCase()
  return PROFILE_AVATAR_BACKGROUND_COLORS.find((color) => color.value.toLowerCase() === normalized) ?? PROFILE_AVATAR_BACKGROUND_COLORS[0]
}

function getProfileAvatarStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function readProfileAvatarPreset(): string {
  const stored = getProfileAvatarStorage()?.getItem(PROFILE_AVATAR_PRESET_STORAGE_KEY) ?? DEFAULT_PROFILE_AVATAR_PRESET
  return resolveProfileAvatarPreset(stored).value
}

function readProfileAvatarBackgroundColor(): string {
  const stored = getProfileAvatarStorage()?.getItem(PROFILE_AVATAR_BACKGROUND_COLOR_STORAGE_KEY) ?? DEFAULT_PROFILE_AVATAR_BACKGROUND_COLOR
  return resolveProfileAvatarBackgroundColor(stored).value
}

function persistProfileAvatarPreset(value: string): void {
  try {
    getProfileAvatarStorage()?.setItem(PROFILE_AVATAR_PRESET_STORAGE_KEY, value)
  } catch {
    // Local storage can be unavailable in restrictive browser modes.
  }
}

function persistProfileAvatarBackgroundColor(value: string): void {
  try {
    getProfileAvatarStorage()?.setItem(PROFILE_AVATAR_BACKGROUND_COLOR_STORAGE_KEY, value)
  } catch {
    // Local storage can be unavailable in restrictive browser modes.
  }
}

export const useUserStore = defineStore('user', {
  state: () => {
    return {
      userName: 'Loading...',
      email: '',
      memberSince: '',
      pfp: 'https://picsum.photos/id/22/200/300',
      avatarPreset: readProfileAvatarPreset(),
      avatarBackgroundColor: readProfileAvatarBackgroundColor(),
      is2FAEnabled: false,
      userId: '',
    }
  },

  getters: {
    selectedProfileAvatar: (state) => resolveProfileAvatarPreset(state.avatarPreset),
    selectedProfileAvatarBackground: (state) => resolveProfileAvatarBackgroundColor(state.avatarBackgroundColor),
  },

  actions: {
    async initialize(fallbackEmail = '') {
      // Only fetch user if a session token exists (i.e., user is logged in)
      const sessionToken = getAdminSessionToken()
      if (!sessionToken) {
        // User not logged in, use defaults
        this.userName = 'Guest'
        this.email = ''
        this.userId = ''
        setAdminEmail('')
        return
      }

      try {
        const currentUser = await fetchCurrentUser()
        this.userName = currentUser.name || 'User'
        this.email = currentUser.email || ''
        this.userId = currentUser.id || ''
        this.memberSince = new Date().toLocaleDateString()
        this.applyProfileAvatarFromUser(currentUser)
        setAdminEmail(this.email)
      } catch (error) {
        const status = Number((error as any)?.response?.status ?? 0)

        // Login may succeed before the RBAC user profile is provisioned.
        // Use a local fallback identity instead of surfacing noisy errors.
        if (status === 404 || status === 401) {
          const normalizedEmail = String(fallbackEmail || this.email || getAdminEmail() || 'arksoftusa@gmail.com').trim()
          this.email = normalizedEmail
          this.userName = normalizedEmail.includes('@') ? normalizedEmail.split('@')[0] : 'Admin'
          this.userId = ''
          this.memberSince = new Date().toLocaleDateString()
          setAdminEmail(this.email)
          return
        }

        console.error('Failed to fetch current user:', error)
      }
    },

    toggle2FA() {
      this.is2FAEnabled = !this.is2FAEnabled
    },

    changeUserName(userName: string) {
      this.userName = userName
    },

    updateUserEmail(email: string) {
      this.email = email.trim().toLowerCase()
      setAdminEmail(this.email)
    },

    applyProfileAvatarFromUser(profile: ProfileAvatarSource) {
      const avatarPreset = String(profile?.avatar_preset ?? '').trim()
      if (avatarPreset) {
        const selectedPreset = resolveProfileAvatarPreset(avatarPreset)
        this.avatarPreset = selectedPreset.value
        persistProfileAvatarPreset(selectedPreset.value)
      }

      const avatarBackgroundColor = String(profile?.avatar_background_color ?? '').trim()
      if (avatarBackgroundColor) {
        const selectedBackground = resolveProfileAvatarBackgroundColor(avatarBackgroundColor)
        this.avatarBackgroundColor = selectedBackground.value
        persistProfileAvatarBackgroundColor(selectedBackground.value)
      }
    },

    async persistCurrentUserProfile() {
      if (!getAdminSessionToken()) {
        return
      }

      try {
        const profile = await saveCurrentUserProfile({
          avatar_preset: this.avatarPreset,
          avatar_background_color: this.avatarBackgroundColor,
        })
        this.applyProfileAvatarFromUser(profile)
      } catch (error) {
        if (Number((error as any)?.response?.status ?? 0) !== 401) {
          console.error('Failed to save profile avatar:', error)
        }
      }
    },

    updateAvatarPreset(avatarPreset: string) {
      const selectedPreset = resolveProfileAvatarPreset(avatarPreset)
      this.avatarPreset = selectedPreset.value
      persistProfileAvatarPreset(selectedPreset.value)
      void this.persistCurrentUserProfile()
    },

    updateAvatarBackgroundColor(backgroundColor: string) {
      const selectedBackground = resolveProfileAvatarBackgroundColor(backgroundColor)
      this.avatarBackgroundColor = selectedBackground.value
      persistProfileAvatarBackgroundColor(selectedBackground.value)
      void this.persistCurrentUserProfile()
    },
  },
})
